import { useCallback } from "react";
import { useQuery, useMutation } from "./urqlHooks";
import { useSelectedCompanyId } from "./tenantCompanyId";

const PASSWORDS = `query PASSWORDS(
    $companyId: String!
    $pagination: PaginationInput!
    $search: String
  ) {
    paginatedPasswordsByCompanyId(
      companyId: $companyId
      pagination: $pagination
      search: $search
    ) {
      totalDocs
      docs {
        id
        name
        templateId
        allowedRoles 
        createdAt
        description
        isActive
      }
    }
  }`;

export const GetPasswords = (companyId, pagination, search) => {
  const [result, reexecuteQuery] = useQuery({
    query: PASSWORDS,
    variables: { companyId, pagination, search },
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const PASSWORD_BY_COMPANY = `query PASSWORDS_BY_COMPANY(
  $companyId: String!
) {
  allPasswordsByCompanyId(
    companyId: $companyId
  ){
    id
    name
    templateId
    currencyType
    allowedRoles 
    createdAt
    description
    isActive
    isUpdating
    isOpenBanking
    botlogSummary{
      id
      botend
      end
      status
      mssg
    }
  }
}`;

export const GetPasswordsByCompany = (companyId) => {
  const [result, reexecuteQuery] = useQuery({
    query: PASSWORD_BY_COMPANY,
    variables: {companyId}
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const CREATE_PASSWORD = `mutation CREATE_PASSWORD(
  $name: String!
  $companyId: String!
  $templateId: TemplateId!
  $allowedRoles: [String!]
  $content: JSONObject
  $encryptedAesKey: String
  $encryptedPayload: String
  $description: String
  $reconcile: Boolean
  $reconcileUntil: String
  $currencyType: SupportedCurrency
) {
  createPassword(
    input: {
      name: $name
      companyId: $companyId
      templateId: $templateId
      allowedRoles: $allowedRoles
      description: $description
      reconcile: $reconcile
      reconcileUntil: $reconcileUntil
      content: $content
      encryptedAesKey: $encryptedAesKey
      encryptedPayload: $encryptedPayload
      currencyType: $currencyType
    }
  ) {
    id
  }
}
`;
export const CreatePassword = () => {
  const [, createPassword] = useMutation(CREATE_PASSWORD);
  return createPassword;
};

const UPDATE_PASSWORD = `mutation UPDATE_PASSWORD(
  $id: String!
  $name: String!
  $companyId: String!
  $templateId: TemplateId!
  $allowedRoles: [String!]
  $content: String
  $encryptedAesKey: String
  $encryptedPayload: String
  $description: String
  $reconcile: Boolean
  $isActive: Boolean
  $isUpdating: Boolean
  $currencyType: SupportedCurrency
) {
  updatePassword(
    id: $id
    input: {
      name: $name
      companyId: $companyId
      templateId: $templateId
      allowedRoles: $allowedRoles
      content: $content
      encryptedAesKey: $encryptedAesKey
      encryptedPayload: $encryptedPayload
      description: $description
      reconcile: $reconcile
      isActive: $isActive
      isUpdating: $isUpdating
      currencyType: $currencyType
    }
  ) {
    id
  }
}`;

export const UpdatePassword = () => {
  const [, updatePassword] = useMutation(UPDATE_PASSWORD);
  return updatePassword;
};

const GET_PASSWORD = `query GET_PASSWORD($id: String!, $companyId: String!) {
  Password(id: $id, companyId: $companyId) {
    id
    templateId
    content
    reconcile
    companyId {
      id
    }
    name
    description
    allowedRoles
    createdAt
    currencyType
  }
}
`;

export const GetSelectedPassword = (id) => {
  const selectedCompanyId = useSelectedCompanyId();
  const [result, reexecuteQuery] = useQuery({
    query: GET_PASSWORD,
    variables: { id, companyId: selectedCompanyId },
    pause: !id || !selectedCompanyId,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const DELETE_PASSWORD = `mutation DELETE_PASSWORD($id: String!, $companyId: String!) {
  deletePassword(id: $id, companyId: $companyId) {
    id
  }
}
`;
export const DeletePassword = () => {
  const selectedCompanyId = useSelectedCompanyId();
  const [, deletePassword] = useMutation(DELETE_PASSWORD);
  return useCallback(
    (vars) => deletePassword({ ...vars, companyId: vars?.companyId || selectedCompanyId }),
    [deletePassword, selectedCompanyId],
  );
};
